import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { AlertTriangle, CheckCircle, TrendingUp } from 'lucide-react';

const RiskStrengthPanel = ({ risks = [], strengths = [] }) => {
  const getSeverityColor = (severity) => {
    const colors = {
      high: 'bg-purple-100 border-purple-300 text-purple-900',
      medium: 'bg-blue-100 border-blue-300 text-blue-900',
      low: 'bg-green-100 border-green-300 text-green-900'
    };
    return colors[severity] || colors.medium;
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {/* Areas for Growth Card */}
      <Card className={`${risks.length > 0 ? 'border-2 border-purple-200 bg-purple-50' : ''}`}>
        <CardHeader className="pb-3">
          <CardTitle className="text-base sm:text-lg flex items-center text-purple-900">
            <TrendingUp className="h-5 w-5 mr-2" />
            Let's Get You Back on Track {risks.length > 0 && `(${risks.length})`}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {risks.length > 0 ? (
            <div className="space-y-3">
              {risks.map((risk, index) => (
                <div 
                  key={index}
                  className={`p-3 rounded-lg border-2 ${getSeverityColor(risk.severity)}`}
                >
                  <div className="flex items-start gap-2">
                    <span className="text-xl flex-shrink-0 mt-0.5">{risk.icon}</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium">{risk.message}</p>
                      {risk.severity === 'high' && (
                        <span className="text-xs font-semibold mt-1 inline-block">
                          We're here to support you
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-6 text-center">
              <CheckCircle className="h-12 w-12 text-green-500 mb-2" />
              <p className="text-sm text-gray-600">
                You're doing great! No areas need immediate attention.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Strengths Card */}
      <Card className={`${strengths.length > 0 ? 'border-2 border-green-200 bg-green-50' : ''}`}>
        <CardHeader className="pb-3">
          <CardTitle className="text-base sm:text-lg flex items-center text-green-900">
            <TrendingUp className="h-5 w-5 mr-2" />
            What's Working Well {strengths.length > 0 && `(${strengths.length})`}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {strengths.length > 0 ? (
            <div className="space-y-3">
              {strengths.map((strength, index) => (
                <div 
                  key={index}
                  className="p-3 rounded-lg bg-green-100 border-2 border-green-300"
                >
                  <div className="flex items-start gap-2">
                    <span className="text-xl flex-shrink-0 mt-0.5">{strength.icon}</span>
                    <p className="text-sm font-medium text-green-900">{strength.message}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-6 text-center">
              <TrendingUp className="h-12 w-12 text-gray-400 mb-2" />
              <p className="text-sm text-gray-600">
                Keep building healthy habits—you'll see your progress here!
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default RiskStrengthPanel;
